import { useCallback, useEffect, useState, type RefObject } from 'react';
import i18next from '@bonanza-social/i18n';
import { View } from 'react-native';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { PREFERRED_LANGUAGE_STORAGE_KEY } from '../constants/languageSwitcher.constants';
import { switchLanguage } from '../state/account/account.slice';

export type Lang = 'en' | 'fr';

export const usePreferredLanguage = (): {
  preferredLanguage: Lang;
  storePreferredLanguage: (lang: Lang) => void;
} => {
  const [preferredLanguage, setPreferredLanguage] = useState<Lang>('en');
  const dispatch = useDispatch();

  useEffect(() => {
    const getPreferredLanguage = async () => {
      const storedPreferred =
        (await AsyncStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY)) ??
        i18next.language;

      setPreferredLanguage(storedPreferred as Lang);
    };

    getPreferredLanguage();
  }, []);

  const storePreferredLanguage = useCallback(
    (lang: Lang) => {
      setPreferredLanguage(lang);
      dispatch(switchLanguage(lang));
    },
    [dispatch],
  );

  return { preferredLanguage, storePreferredLanguage };
};

export function useOutsidePress(
  isOpen: boolean,
  containerRef: RefObject<View | null>,
  onOutsidePress: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;

    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      const current = containerRef.current as unknown as Node | null;
      if (!current) return;

      if (target && current.contains(target)) return;

      onOutsidePress();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [containerRef, isOpen, onOutsidePress]);
}
